import { excludeInternalSupportEmployees } from '../../utils/employeeVisibility';
import React, { useState, useEffect } from "react";
import { Grid, Button, Autocomplete, TextField, Typography, Snackbar, Alert, IconButton, Tooltip } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGridPro } from '@mui/x-data-grid-pro';
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../config";

export default function ScheduleReports(props) {
    const [loading, setLoading] = useState(true);
    const [dashboardList, setDashboardList] = useState([]);
    const [selectedDashboard, setSelectedDashboard] = useState(null);
    const [frequencyList, setFrequencyList] = useState([]);
    const [selectedFrequency, setSelectedFrequency] = useState(null);
    const [recipientList, setRecipientList] = useState([]);
    const [selectedRecipients, setSelectedRecipients] = useState([]);
    const [addingSchedule, setAddingSchedule] = useState(false);
    const [reportSchedules, setReportSchedules] = useState([]);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');

    useEffect(() => {
        fetchDashboards();
        fetchRecipientData();
        fetchReportScheduleData();

        setLoading(false);
    }, []);

    const fetchReportScheduleData = async () => {
        await axios.get(APIURL + "/report_schedule", {
            params: { compID: props.comp_id, empID: props.user.emp_id, empType: props.user.type },
        }).then((response) => {
            setReportSchedules(response.data);
        }).catch((error) => {
            console.log(error);
        });
    };

    const fetchDashboards = async () => {
        await axios.get(APIURL + "/report_schedule/schedulable", {
            params: {},
        }).then((response) => {
            setDashboardList(response.data);
        }).catch((error) => {
            console.log(error);
        });

        setFrequencyList([
            { frequency_id: 0, frequency_name: 'Daily' },
            { frequency_id: 1, frequency_name: 'Weekly' },
            { frequency_id: 2, frequency_name: 'Monthly' }
        ]);
    };

    const fetchRecipientData = async () => {
        if (props.user.type === 'Admin') {
            await axios.get(APIURL + '/dropdowns/dropdownType/employeeslist', {
                params: { comp_id: props.user.comp_id }
            }).then((response) => {
                const empList = excludeInternalSupportEmployees(response.data).map((emp) => { return { emp_id: emp.id, emp_name: emp.employee_name } });
                setRecipientList(empList);
            }).catch((error) => {
                console.log(error);
            });
        } else if (props.user.type.includes('Manager')) {
            await axios.get(APIURL + "/employees/v2/associated", {
                params: { company_id: props.user.comp_id, employee_id: props.user.emp_id },
            }).then((response) => {
                let associatedEmployees = response.data.directlyManaged.concat(response.data.hr_managed).concat({ emp_id: props.user.emp_id, emp_name: props.user.emp_name, emp_is_internal_support: props.user.emp_is_internal_support });

                associatedEmployees = excludeInternalSupportEmployees(associatedEmployees).filter((obj, index, self) => {
                    return index === self.findIndex((t) => (
                        t.emp_id === obj.emp_id
                    ));
                }).map((emp) => { return { emp_id: emp.emp_id, emp_name: emp.emp_name ? emp.emp_name : emp.emp_first_name + ' ' + emp.emp_last_name } });

                setRecipientList(associatedEmployees);
            }).catch((error) => {
                console.log(error);
            });
        } else {
            setRecipientList(excludeInternalSupportEmployees([{ emp_id: props.user.emp_id, emp_name: props.user.emp_name, emp_is_internal_support: props.user.emp_is_internal_support }]));
        }
    };

    const onSchedule = async () => {
        setAddingSchedule(true);

        await axios
            .post(APIURL + `/report_schedule`, {
                report_id: selectedDashboard.dashboard_id,
                schedule_recipient: selectedRecipients.map(emp => emp.emp_id),
                schedule_frequency: selectedFrequency.frequency_name,
                schedule_companyID: props.comp_id,
                schedule_createdBy: props.user.emp_id,
            })
            .then(function (response) {
                setAlertMessage('Schedule added successfully!');
                setAlertType('success');
            })
            .catch(function (error) {
                console.log(error);
                setAlertMessage('An error occurred while scheduling!');
                setAlertType('error');
            });
    };

    const onDelete = async (report_schedule_id) => {
        await axios
            .delete(APIURL + `/report_schedule`, {
                params: {
                    report_schedule_id: report_schedule_id,
                }
            }).then((response) => {
                setAlertMessage('Schedule deleted successfully!');
                setAlertType('success');
            }).catch((error) => {
                console.error(error);
                setAlertMessage('An error occurred while deleting schedule!');
                setAlertType('error');
            });
    };

    const SnackbarComponent = () => {
        return (
            <div>
                <Snackbar open={alertMessage ? true : false} autoHideDuration={2000} onClose={handleSnackbarAlertClose}>
                    <Alert severity={alertType} elevation={6} variant="filled">
                        {alertMessage}
                        <IconButton
                            sx={{ p: 0.25 }}
                            onClick={handleSnackbarAlertClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Alert>
                </Snackbar>
            </div>
        );
    };

    const handleSnackbarAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertMessage('');
        setAlertType('');
        setSelectedDashboard(null);
        setSelectedRecipients([]);
        setSelectedFrequency(null);
        setAddingSchedule(false);
        fetchReportScheduleData();
    };

    const columns = [
        {
            field: "actions",
            headerName: "Actions",
            width: 75,
            pinnable: true,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Tooltip title="Delete Schedule">
                    <IconButton
                        color="error"
                        size="small"
                        onClick={() => { onDelete(params.row.report_schedule_id); }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
        { field: "report_schedule_id", headerName: "Report Schedule ID", width: 75 },
        { field: "report_id", headerName: "Report ID", width: 75 },
        { field: "report_name", headerName: "Report Name", width: 150 },
        { field: "schedule_frequency", headerName: "Frequency", width: 100 },
        { field: "schedule_recipient", headerName: "Recipient", width: 150 },
        { field: "nextDelivery", headerName: "Next Delivery", width: 200 },
        { field: "schedule_createdBy", headerName: "Created By", width: 150 },
        { field: "schedule_createdAt", headerName: "Created At", width: 100 },
    ];

    const rows = reportSchedules.map((data, index) => {
        let nextDelivery = null;

        if (data.schedule_frequency === 'Daily') {
            // Every day at 7:00 PM
            let nd = moment().hour(19).minute(0).second(0);
            if (nd.isBefore(moment())) {
                nd.add(1, 'day');
            }
            nextDelivery = nd.format('MM/DD/YYYY hh:mm A');
        }
        else if (data.schedule_frequency === 'Weekly') {
            // End of week (Sunday) at 7:00 PM
            let nd = moment().day(7).hour(19).minute(0).second(0);
            if (nd.isBefore(moment())) {
                nd.add(1, 'week');
            }
            nextDelivery = nd.format('MM/DD/YYYY hh:mm A');
        }
        else if (data.schedule_frequency === 'Monthly') {
            // End of month at 7:00 PM
            let nd = moment().endOf('month').hour(19).minute(0).second(0);
            if (nd.isBefore(moment())) {
                nd.add(1, 'month');
            }
            nextDelivery = nd.format('MM/DD/YYYY hh:mm A');
        }

        const schedule = {
            id: index,
            report_schedule_id: data.report_schedule_id,
            report_id: data.report_id,
            report_name: data.report_name,
            schedule_frequency: data.schedule_frequency,
            schedule_recipient: data.recipientName,
            schedule_createdBy: data.createdBy,
            schedule_createdAt: moment(data.schedule_createdAt).format('MM/DD/YYYY hh:mm A'),
            nextDelivery: nextDelivery,
        };
        return schedule;
    });

    return (
        <div style={{ width: '100%' }}>
            <div>
                <Typography
                    style={{
                        fontWeight: "bold",
                        fontSize: "1.5rem",
                        fontFamily: "Lato",
                        marginBottom: 10,
                        marginLeft: 10
                    }}
                >
                    Schedule Reports
                </Typography>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 0', margin: 10 }}>
                <div style={{ marginRight: 10 }}>
                    <Autocomplete
                        id="dashboard-select"
                        options={dashboardList}
                        value={selectedDashboard}
                        getOptionLabel={(option) => option.dashboard_name}
                        isOptionEqualToValue={(option, value) => option.dashboard_id === value.dashboard_id}
                        onChange={(event, value) => { setSelectedDashboard(value); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.dashboard_id}>
                                {option.dashboard_name}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Dashboard" />}
                        size="small"
                        sx={{ width: 250 }}
                    />
                </div>
                <div style={{ marginRight: 10 }}>
                    <Autocomplete
                        id="recipient-select"
                        multiple
                        options={recipientList}
                        value={selectedRecipients}
                        getOptionLabel={(option) => option.emp_name}
                        isOptionEqualToValue={(option, value) => option.emp_id === value.emp_id}
                        onChange={(event, value) => { setSelectedRecipients(value); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.emp_id}>
                                {option.emp_name}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Recipients" />}
                        size="small"
                        sx={{ width: 550 }}
                    />
                </div>
                <div style={{ marginRight: 10 }}>
                    <Autocomplete
                        id="frequency-select"
                        options={frequencyList}
                        value={selectedFrequency}
                        getOptionLabel={(option) => option.frequency_name}
                        isOptionEqualToValue={(option, value) => option.frequency_id === value.frequency_id}
                        onChange={(event, value) => { setSelectedFrequency(value); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.frequency_id}>
                                {option.frequency_name}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Frequency" />}
                        size="small"
                        sx={{ width: 200 }}
                    />
                </div>
                <Grid style={{ marginLeft: 40 }} size={1}>
                    <Button
                        onClick={() => { onSchedule(); }}
                        variant="contained"
                        disabled={addingSchedule || !selectedDashboard || !selectedRecipients.length || !selectedFrequency}
                    >
                        SCHEDULE
                    </Button>
                </Grid>
            </div>
            <div style={{ paddingLeft: 10, width: '100%', height: '80vh' }}>
                <DataGridPro
                    initialState={{
                        sorting: { sortModel: [{ field: "schedule_createdAt", sort: "desc" }] },
                        pinnedColumns: { left: ['actions'] },
                    }}
                    getRowId={(row) => row.id}
                    rows={rows}
                    columns={columns}
                    disableRowSelectionOnClick
                    sx={{
                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            textAlign: 'left',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                        },
                    }}
                    loading={loading}
                    showToolbar
                />
            </div>
            {SnackbarComponent()}
        </div>
    )
};